import { useState, useEffect } from "react";
import { Users, Mail, Building2, Clock, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import apiClient from "../utils/apiClient";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-800" },
  { value: "qualified", label: "Qualified", color: "bg-green-100 text-green-800" },
  { value: "converted", label: "Converted", color: "bg-emerald-100 text-emerald-800" },
  { value: "closed", label: "Closed", color: "bg-gray-100 text-gray-700" },
];

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${opt.color}`} data-testid={`lead-status-${status}`}>
      {opt.label}
    </span>
  );
};

const AdminSalesLeads = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const res = await apiClient.get(`/admin/sales-leads${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchLeads(); }, [filterStatus]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await apiClient.put(`/admin/sales-leads/${leadId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
      );
    } catch { /* ignore */ }
  };

  const usageLabel = { light: "Light", moderate: "Moderate", heavy: "Heavy", not_sure: "Not sure" };
  const timelineLabel = { immediate: "Immediate", "1-3_months": "1–3 months", "3-6_months": "3–6 months", exploring: "Exploring" };

  return (
    <div className="space-y-6" data-testid="admin-sales-leads">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Sales Leads</h2>
          <p className="text-sm text-muted-foreground">{total} total submissions</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36" data-testid="filter-status">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-12">Loading...</p>
      ) : leads.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No leads found.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <Card key={lead.id} className="overflow-hidden" data-testid={`lead-card-${lead.id}`}>
              <div
                className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{lead.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{lead.organization} — {lead.job_title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <StatusBadge status={lead.status} />
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedId === lead.id ? "rotate-180" : ""}`} />
                </div>
              </div>

              {expandedId === lead.id && (
                <div className="border-t border-border px-5 py-4 space-y-4 bg-muted/10">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground block mb-0.5">Email</span>
                      <a href={`mailto:${lead.email}`} className="text-foreground hover:underline">{lead.email}</a>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-0.5">Team Size</span>
                      <span>{lead.team_size}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-0.5">Usage</span>
                      <span>{usageLabel[lead.expected_usage] || lead.expected_usage}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block mb-0.5">Timeline</span>
                      <span>{timelineLabel[lead.timeline] || lead.timeline}</span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block mb-0.5">Use Case</span>
                    <span>{lead.primary_use_case}</span>
                  </div>
                  {lead.notes && (
                    <div className="text-xs">
                      <span className="text-muted-foreground block mb-0.5">Notes</span>
                      <span className="whitespace-pre-wrap">{lead.notes}</span>
                    </div>
                  )}
                  {lead.plan_key && (
                    <div className="text-xs">
                      <span className="text-muted-foreground block mb-0.5">Plan Interest</span>
                      <span className="capitalize">{lead.plan_key}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-muted-foreground mr-1">Status:</span>
                    {STATUS_OPTIONS.map((s) => (
                      <Button
                        key={s.value}
                        variant={lead.status === s.value ? "default" : "outline"}
                        size="sm"
                        className="h-6 text-xs px-2"
                        onClick={() => updateStatus(lead.id, s.value)}
                        data-testid={`set-status-${s.value}-${lead.id}`}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSalesLeads;
